import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import api, { getImageUrl } from '../../utils/api';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';

export default function BrokerProfile() {
    const { user, token, updateUser } = useAuth();
    const { theme } = useTheme();
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const [form, setForm] = useState({
        name: '', phone: '', title: '', address: '', city: '', state: '', zip: '', about: '',
        facebook: '', twitter: '', googleplus: '', linkedin: ''
    });

    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

    useEffect(() => {
        if (user) {
            setForm({
                name: user.name || '',
                phone: user.phone || '',
                title: user.title || '',
                address: user.address || '',
                city: user.city || '',
                state: user.state || '',
                zip: user.zip || '',
                about: user.about || '',
                facebook: user.facebook || '',
                twitter: user.twitter || '',
                googleplus: user.googleplus || '',
                linkedin: user.linkedin || ''
            });
            if (user.avatar) {
                setAvatarPreview(getImageUrl(user.avatar));
            }
        }
    }, [user]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setAvatarFile(file);
            setAvatarPreview(URL.createObjectURL(file));
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token) return;
        setSaving(true);
        setMsg(null);

        let finalAvatar = user?.avatar;

        try {
            if (avatarFile) {
                const uploadRes = await api.uploadImages(token, [avatarFile]);
                if (uploadRes.urls && uploadRes.urls.length > 0) {
                    finalAvatar = uploadRes.urls[0];
                } else {
                    throw new Error('Avatar upload failed');
                }
            }

            const data = await api.updateMe(token, { ...form, avatar: finalAvatar });
            if (data._id) {
                updateUser(data);
                setMsg({ type: 'success', text: 'Broker profile updated successfully!' });
            } else {
                setMsg({ type: 'error', text: data.message || 'Failed to update profile' });
            }
        } catch (err: any) {
            setMsg({ type: 'error', text: err.message || 'Server error. Please try again.' });
        }
        setSaving(false);
    };

    return (
        <UserDashboardLayout>
            <div className="max-w-5xl mx-auto py-8">
                <div className="mb-12 px-4 animate__animated animate__fadeInDown">
                    <h1 className="text-5xl md:text-6xl font-bold text-[var(--text-primary)] mb-3">System Identity</h1>
                    <p className="text-[var(--text-secondary)] font-bold text-sm opacity-70">Manage your professional cryptographic profile & contact nodes.</p>
                </div>

                {msg && (
                    <div className={`mx-4 p-6 rounded-[2rem] mb-10 flex items-center gap-4 border animate__animated animate__shakeX ${
                        msg.type === 'success' 
                        ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20 shadow-lg shadow-emerald-500/5' 
                        : 'bg-rose-500/10 text-rose-500 border-rose-500/20 shadow-lg shadow-rose-500/5'
                    }`}>
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${msg.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white shadow-lg'}`}>
                            <i className={`fa-solid ${msg.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'} text-lg`}></i>
                        </div>
                        <span className="font-black uppercase tracking-widest text-xs">{msg.text}</span>
                    </div>
                )}

                <form onSubmit={handleSave} className="space-y-10 px-4">
                    {/* Identity Glass Card */}
                    <div className="glass-card p-8 md:p-12 relative overflow-hidden group animate__animated animate__fadeIn">
                        <div className="absolute -top-24 -right-24 w-80 h-80 bg-[var(--purple)]/10 rounded-full blur-3xl group-hover:bg-[var(--purple)]/20 transition-all duration-1000"></div>
                        
                        <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-10 flex items-center gap-4">
                            <i className="fa-solid fa-id-card-clip text-[var(--purple)] text-2xl"></i>
                            Personal Data Matrix
                        </h3>
                        
                        <div className="flex flex-col lg:flex-row gap-12 relative z-10">
                            {/* Avatar Module */}
                            <div className="flex flex-col items-center gap-6">
                                <div className="relative group/avatar">
                                    <div className="w-48 h-48 rounded-[2.5rem] overflow-hidden bg-[var(--bg-main)] border-2 border-[var(--purple)]/20 shadow-2xl relative transition-all group-hover/avatar:border-[var(--purple)] group-hover/avatar:scale-105 duration-500">
                                        {avatarPreview ? (
                                            <img src={avatarPreview} className="w-full h-full object-cover group-hover/avatar:scale-110 transition-transform duration-700" alt="Avatar" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-[var(--purple)]/20">
                                                <i className="fa-solid fa-user text-6xl"></i>
                                            </div>
                                        )}
                                        <label className="absolute inset-0 bg-gradient-to-t from-[var(--purple)]/80 to-transparent flex items-center justify-center opacity-0 group-hover/avatar:opacity-100 transition-opacity cursor-pointer backdrop-blur-sm">
                                            <input type="file" className="hidden" accept="image/*" onChange={handleAvatarChange} />
                                            <div className="bg-white p-4 rounded-2xl shadow-2xl transform translate-y-4 group-hover/avatar:translate-y-0 transition-transform duration-500">
                                                <i className="fa-solid fa-camera text-[var(--purple)] text-xl"></i>
                                            </div>
                                        </label>
                                    </div>
                                    <div className="absolute -bottom-4 -right-4 w-12 h-12 bg-[var(--gold)] rounded-2xl flex items-center justify-center text-white shadow-xl border-4 border-[var(--bg-card)]">
                                        <i className="fa-solid fa-pen text-xs"></i>
                                    </div>
                                </div>
                                <p className="text-xs font-bold text-[var(--text-secondary)] opacity-50">Upload Node Image</p>
                            </div>

                            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-3">
                                    <label className="text-xs font-bold text-[var(--text-secondary)] ml-2 opacity-60">Full Identity Name</label>
                                    <input type="text" className="w-full px-6 py-4 bg-[var(--bg-main)] border border-[var(--purple)]/10 rounded-2xl focus:border-[var(--purple)] outline-none transition-all font-bold text-base text-[var(--text-primary)] shadow-inner" name="name" value={form.name} onChange={handleChange} />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-xs font-bold text-[var(--text-secondary)] ml-2 opacity-60">Registered Email</label>
                                    <input type="email" className="w-full px-6 py-4 bg-[var(--bg-main)]/50 border border-[var(--purple)]/5 rounded-2xl text-[var(--text-secondary)] font-bold text-base cursor-not-allowed opacity-50" value={user?.email || ''} readOnly disabled />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-xs font-bold text-[var(--text-secondary)] ml-2 opacity-60">Professional Title</label>
                                    <input type="text" className="w-full px-6 py-4 bg-[var(--bg-main)] border border-[var(--purple)]/10 rounded-2xl focus:border-[var(--purple)] outline-none transition-all font-bold text-base text-[var(--text-primary)] shadow-inner" name="title" value={form.title} onChange={handleChange} placeholder="Elite Principal Broker" />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-xs font-bold text-[var(--text-secondary)] ml-2 opacity-60">Direct Contact Node</label>
                                    <input type="text" className="w-full px-6 py-4 bg-[var(--bg-main)] border border-[var(--purple)]/10 rounded-2xl focus:border-[var(--purple)] outline-none transition-all font-bold text-base text-[var(--text-primary)] shadow-inner" name="phone" value={form.phone} onChange={handleChange} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Location Glass Card */}
                    <div className="glass-card p-8 md:p-12 relative overflow-hidden group animate__animated animate__fadeIn">
                        <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-10 flex items-center gap-4">
                            <i className="fa-solid fa-map-location-dot text-[var(--gold)] text-2xl"></i>
                            Agency Geospatial Data
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10">
                            <div className="space-y-3 md:col-span-2">
                                <label className="text-xs font-bold text-[var(--text-secondary)] ml-2 opacity-60">Operational Base Address</label>
                                <input type="text" className="w-full px-6 py-4 bg-[var(--bg-main)] border border-[var(--purple)]/10 rounded-2xl focus:border-[var(--purple)] outline-none transition-all font-bold text-base text-[var(--text-primary)] shadow-inner" name="address" value={form.address} onChange={handleChange} />
                            </div>
                            <div className="space-y-3">
                                <label className="text-xs font-bold text-[var(--text-secondary)] ml-2 opacity-60">City Node</label>
                                <input type="text" className="w-full px-6 py-4 bg-[var(--bg-main)] border border-[var(--purple)]/10 rounded-2xl focus:border-[var(--purple)] outline-none transition-all font-bold text-base text-[var(--text-primary)] shadow-inner" name="city" value={form.city} onChange={handleChange} />
                            </div>
                            <div className="space-y-3">
                                <label className="text-xs font-bold text-[var(--text-secondary)] ml-2 opacity-60">State/Region</label>
                                <input type="text" className="w-full px-6 py-4 bg-[var(--bg-main)] border border-[var(--purple)]/10 rounded-2xl focus:border-[var(--purple)] outline-none transition-all font-bold text-base text-[var(--text-primary)] shadow-inner" name="state" value={form.state} onChange={handleChange} />
                            </div>
                            <div className="space-y-3 md:col-span-2">
                                <label className="text-xs font-bold text-[var(--text-secondary)] ml-2 opacity-60">Agency Core Mission / Intel</label>
                                <textarea className="w-full px-6 py-5 bg-[var(--bg-main)] border border-[var(--purple)]/10 rounded-[2rem] focus:border-[var(--purple)] outline-none transition-all font-bold text-[var(--text-primary)] min-h-[160px] resize-none shadow-inner leading-relaxed" name="about" value={form.about} onChange={handleChange} placeholder="Synchronize your professional background with our network..."></textarea>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-center pt-6 pb-12">
                        <button 
                            className="btn-purple px-16 py-5 no-underline group shadow-2xl relative overflow-hidden" 
                            type="submit" 
                            disabled={saving}
                        >
                            <span className="relative z-10 flex items-center justify-center gap-4">
                                {saving ? <i className="fa-solid fa-circle-notch fa-spin"></i> : <i className="fa-solid fa-cloud-arrow-up group-hover:-translate-y-1 transition-transform"></i>}
                                {saving ? 'Encrypting Data...' : 'Synchronize Identity Core'}
                            </span>
                        </button>
                    </div>
                </form>
            </div>
        </UserDashboardLayout>
    );
}
